import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { AUTH_ROUTES } from 'app/modules/auth/constants/auth.routes';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { redact } from 'app/shared/utils/sensitive-keys';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

/**
 * Auth is an httpOnly cookie set by the API (salis-be WebAuthCookie). Every
 * API call goes out withCredentials + `X-Client-App: admin` - the header is
 * what lets the API use the cookie (CSRF protection), and tells it which
 * app's cookie to read/write.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone = new BehaviorSubject<boolean | null>(null);

  constructor(
    private router: Router,
    private injector: Injector,
    private logger: LoggerService
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    req = this.withAuth(req);

    const startedAt = Date.now();
    this.logger.debug('API', `-> ${req.method} ${req.url}`, redact(req.body));

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.logger.debug(
            'API',
            `<- ${event.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
            redact(event.body)
          );
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.warn(
          'API',
          `<- ${error.status} ${req.method} ${req.url} (${Date.now() - startedAt}ms)`,
          redact(error.error)
        );

        if (error.status >= 500) {
          Sentry.captureMessage(`API server error: ${req.method} ${req.url}`, {
            level: 'error',
            extra: { status: error.status, response: redact(error.error) },
          });
        }

        // Signed in but this session hasn't passed 2FA (e.g. after a role
        // change, or the step was skipped): send the admin to it.
        if (error.status === 403 && error.error?.error_code === 'mfa_required') {
          this.authService.markTwoFactorRequired();
          this.router.navigate([AUTH_ROUTES.BASE, AUTH_ROUTES.TWO_FACTOR]);
          return throwError(() => error);
        }

        if (error.status === 401 && !req.context.get(SKIP_AUTH_REFRESH) && this.authService.hasValidToken()) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private withAuth(req: HttpRequest<any>): HttpRequest<any> {
    if (!req.url.startsWith(environment.apiUrl)) {
      return req;
    }

    // Re-derived on every pass (including retries after a refresh), so a
    // legacy token that was just exchanged for a cookie isn't sent again.
    let headers = req.headers.delete('Authorization').set('X-Client-App', 'admin');
    // One-time migration from pre-cookie builds (AuthService.migrateLegacyToken).
    const legacyToken = this.authService.getLegacyToken();
    if (legacyToken) {
      headers = headers.set('Authorization', `Bearer ${legacyToken}`);
    }

    return req.clone({ withCredentials: true, headers });
  }

  private handle401(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshDone.next(true);
          // The browser now holds the rotated cookie; just replay the request.
          return next.handle(this.withAuth(req));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          // Only a definitive rejection ends the session; a network error,
          // 429 or 5xx during refresh keeps the session for the next attempt.
          if (error?.status === 401 || error?.status === 403) {
            this.authService.clearAuthData();
            this.router.navigate([AUTH_ROUTES.BASE, AUTH_ROUTES.LOGIN]);
          }
          // Release requests queued behind this refresh instead of leaving them hanging.
          this.refreshDone.error(error);
          this.refreshDone = new BehaviorSubject<boolean | null>(null);
          return throwError(() => error);
        })
      );
    }

    return this.refreshDone.pipe(
      filter((done) => done !== null),
      take(1),
      switchMap(() => next.handle(this.withAuth(req)))
    );
  }
}
